import { useEffect, useRef, useState } from 'react';
import type { ApiClient } from '../api';
import { usePoll } from '../hooks';
import { normaliseBrands, startListening, type ListenSession } from '../listen';
import type { ChatRequest } from '../types';

/**
 * A.F.R.O.B.O.T as a floating action button (Matthew, 4 September 2026:
 * "it represents the brain of what we do"). It sits above every view
 * rather than inside the home grid, is draggable, and opens the morning
 * brief.
 *
 * WHAT IT MAY DO is unchanged by moving it. Nothing here executes:
 * the Shell is read-only bar the approval queue, and execution belongs
 * to the MCP permission layer under the owning server's credential
 * (§2 rules 1 and 2). Asking opens the chat; anything that would write
 * or publish becomes a proposal in the approval queue.
 *
 * POSITION IS NOT REMEMBERED between loads. Persisting it would mean
 * browser storage, which is a hard stop for Shell artefacts, and a
 * server-side write for a button's coordinates would be a spec change
 * to the read-only rule. It returns to the top right on reload.
 *
 * THE MIC streams to Transcribe in eu-west-2, not to the browser's own
 * SpeechRecognition, which in Chrome ships the audio to a third party.
 * The questions asked here carry revenue and brand performance, so the
 * speech stays in region like the numbers it is about (rule 8). Audio
 * goes from the microphone to Transcribe and is never stored, by us or
 * on the way: what we keep is the text, and only because asking an
 * agent a question is already a recorded act in the chat table.
 *
 * SPEAKING DOES NOT WIDEN WHAT IT MAY DO. A transcript takes the same
 * path a typed question takes - it becomes a row in chat, answered by
 * the agent under its own credential. There is no spoken command that
 * writes, publishes or approves, because there is no such typed command
 * either.
 *
 * SPEAKING is wired, through /api/speak, which is Polly in eu-west-2
 * under the API's own credential - the browser gets audio, never a key.
 * The voice is a PLACEHOLDER and the control says so: the brief calls
 * for a Black British male voice, no provider publishes the ethnicity
 * behind a stock voice, and the real answer is a licensed or recorded
 * person. Off by default, because a dashboard that talks when it is
 * opened in a meeting is a bug.
 */
/**
 * Questions a tap asks: the ones the dashboard is most often opened for.
 * Each is answered instantly by the dashboard itself (GET /api/quick,
 * 28 October 2026: "speed up the A.F.R.O.B.O.T voice response times"),
 * not by waking the agent, which takes twenty seconds or more.
 */
export const SUGGESTIONS = [
  { label: 'How was today?', topic: 'today' },
  { label: 'How many app downloads today?', topic: 'downloads' },
  { label: 'Any support tickets waiting?', topic: 'tickets' },
  { label: "What's on this week?", topic: 'week' },
] as const;

/** Statuses that mean the agent is finished, matching the chat view. */
const TERMINAL = new Set(['answered', 'failed', 'refused']);

const EDGE = 16;
const SIZE = 60;
/**
 * On a phone held upright (Matthew, 8 October 2026: "keep the FAB usable
 * on vertical mode") the rail is a tab bar along the bottom, so the
 * button starts bottom right just above it and is kept out from under it
 * when dragged; the panel opens as a sheet above the bar (theme.css).
 */
const PHONE = '(max-width: 720px)';
const TAB_BAR = 76;
const isPhone = () => typeof window !== 'undefined' && !!window.matchMedia?.(PHONE).matches;
const maxY = () => window.innerHeight - SIZE - EDGE - (isPhone() ? TAB_BAR : 0);
const startPos = () =>
  isPhone()
    ? { x: window.innerWidth - SIZE - EDGE, y: maxY() }
    : { x: window.innerWidth - SIZE - EDGE, y: 84 };
/** Below this, a pointer moved because a hand is not a machine, not
 *  because someone meant to drag. Without it a 1px wobble between
 *  pointerdown and click swallows the click entirely, which is exactly
 *  what happened the first time this shipped (4 September 2026). */
const DRAG_THRESHOLD = 5;

type Point = { x: number; y: number };

export function AfrobotFab({ api, onAsk }: { api: ApiClient; onAsk: (prompt: string) => void }) {
  const [pos, setPos] = useState<Point | null>(null);
  const [open, setOpen] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [speakError, setSpeakError] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [listening, setListening] = useState(false);
  const [heard, setHeard] = useState('');
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState<ChatRequest | null>(null);
  const [listenError, setListenError] = useState<string | null>(null);
  const [blockedAnswer, setBlockedAnswer] = useState<string | null>(null);
  const [stillWorking, setStillWorking] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const listenRef = useRef<ListenSession | null>(null);
  /** Everything heard this session: settled phrases plus the partial in
   *  flight. Refs, not state, because the socket callbacks that write
   *  them race React's render cycle. */
  const finalsRef = useRef<string[]>([]);
  const partialRef = useRef('');
  const submittedRef = useRef(false);
  const settleTimerRef = useRef<number | undefined>(undefined);
  /** Re-entrancy latch: startListening awaits a signed URL and a
   *  permission prompt, and a second click during either would open a
   *  second microphone session with no handle to stop the first. */
  const micBusyRef = useRef(false);
  /** The panel's open state, readable from inside ask()'s loop: the
   *  closure would otherwise hold the value from the render the click
   *  happened in, and speak an answer into a panel closed minutes ago. */
  const openRef = useRef(false);
  const dragging = useRef(false);
  const moved = useRef(false);
  const grab = useRef<Point>({ x: 0, y: 0 });
  const origin = useRef<Point>({ x: 0, y: 0 });

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const heardSoFar = () =>
    normaliseBrands([...finalsRef.current, partialRef.current].filter(Boolean).join(' ').trim());

  /**
   * One submission per session, from wherever it comes first: the settle
   * timer after the last phrase, or the user pressing the button. A
   * straggling result after this is part of the question already asked,
   * not a new one.
   */
  const submitQuestion = () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    window.clearTimeout(settleTimerRef.current);
    listenRef.current?.stop();
    listenRef.current = null;
    setListening(false);
    const text = heardSoFar();
    if (text) void ask(text);
  };

  /**
   * The other half of "speak back": a spoken question deserves a spoken
   * answer, through the same voice that reads the brief. Played without a
   * press only because the user just spoke - the ban on a dashboard that
   * talks unprompted stands, so a panel closed while the agent was
   * thinking stays silent. Chrome permits audible playback here because
   * the user has interacted with the page; if it refuses anyway, the
   * voice waits behind a button instead of being lost.
   */
  const speakAnswer = async (text: string) => {
    // Every path that does not end in sound must end in the "Hear the
    // answer" button instead: a skipped voice that leaves only text looks
    // exactly like a broken one, which is how it was reported.
    if (!openRef.current) {
      setBlockedAnswer(text);
      return;
    }
    try {
      audioRef.current?.pause();
      const blob = await api.postForBlob('/api/speak', { text });
      const url = URL.createObjectURL(blob);
      // Checked again after the fetch: synthesis takes long enough for
      // the panel to be closed in the meantime, and a closed panel that
      // starts talking is the unprompted-speech bug by another route.
      if (!openRef.current) {
        URL.revokeObjectURL(url);
        setBlockedAnswer(text);
        return;
      }
      const el = new Audio(url);
      audioRef.current = el;
      el.onended = () => {
        setSpeaking(false);
        URL.revokeObjectURL(url);
      };
      await el.play();
      setBlockedAnswer(null);
      setSpeaking(true);
    } catch {
      setBlockedAnswer(text);
    }
  };

  /**
   * What gets SPOKEN is the head of the answer, cut at a sentence: the
   * agent's full text can run to pages, which is minutes of synthesis
   * and then of listening. The full answer stays on screen; the voice
   * says so when it stops early. House style leads with the answer, so
   * the head is the substance, not a preamble.
   */
  const spokenLead = (text: string): string => {
    // Long enough for a full rundown read out in full (29 October 2026:
    // "read us the full run down of the sellers from worst to best");
    // Polly's own cap is 3000.
    const LIMIT = 2600;
    if (text.length <= LIMIT) return text;
    const head = text.slice(0, LIMIT);
    const cut = Math.max(head.lastIndexOf('. '), head.lastIndexOf('? '), head.lastIndexOf('! '));
    return `${cut > 80 ? head.slice(0, cut + 1) : head} The rest is on screen.`;
  };

  /**
   * A spoken question goes where a typed one goes: POST /api/chat, then
   * poll that request until it settles. The agent runs in the worker under
   * its own credential; this only watches. The answering agent is the
   * Reporting agent, A.F.R.O.B.O.T itself (28 October 2026, Matthew: "It
   * needs to be able to know everything so I could ask it how many
   * downloads we had today"): it reads the dashboard's numbers through
   * mcp-reporting. It was Community until then, which could see social
   * inboxes and nothing else, so a question about the numbers had nothing
   * to answer with. The full agent picker lives in the chat view.
   *
   * A tapped suggestion takes the same path (spoken false, so the audit
   * trail says it was typed) and is answered here, aloud, rather than
   * opening an empty Chat tab, which is what the chips did before.
   */
  const ask = async (question: string, spoken = true) => {
    setAsking(true);
    setStillWorking(false);
    setBlockedAnswer(null);
    setElapsed(0);
    try {
      let request = await api.post<ChatRequest>('/api/chat', {
        agent: 'reporting',
        question,
        // Spoken questions run on the cheap fast tier and come back
        // shaped for the ear; the typed chat panel keeps the workhorse.
        spoken,
      });
      setAnswer(request);
      // Three minutes, not ninety seconds: a run with tool calls has
      // taken 45 seconds today, and giving up while the agent is mid
      // thought reads as a dead button.
      const started = Date.now();
      const deadline = started + 180_000;
      while (!TERMINAL.has(request.status) && Date.now() < deadline) {
        // Under a second: the answer is read aloud the moment it lands.
        await new Promise((r) => setTimeout(r, 700));
        setElapsed(Math.round((Date.now() - started) / 1000));
        try {
          request = await api.get<ChatRequest>(`/api/chat/${request.id}`);
          setAnswer(request);
        } catch {
          // One dropped poll out of a hundred must not abort the ask and
          // claim the question was never sent - it was, the row exists.
        }
      }
      if (!TERMINAL.has(request.status)) {
        setStillWorking(true);
      } else {
        // Speak whatever the bubble shows, kept short for the failure
        // cases: a reason is worth hearing, a stack trace is not.
        const line =
          request.status === 'answered' && request.answer
            ? spokenLead(request.answer)
            : request.error && request.error.length <= 240
              ? request.error
              : 'I could not answer that one.';
        if (line) void speakAnswer(line);
      }
    } catch {
      setListenError('The question could not be put to the agent.');
    } finally {
      setAsking(false);
    }
  };

  /** A suggestion, answered by the dashboard in under a second; the agent only if that fails. */
  const quick = async (label: string, topic: string) => {
    setAsking(true);
    setStillWorking(false);
    setBlockedAnswer(null);
    try {
      const { text } = await api.get<{ text: string }>(`/api/quick/${topic}`);
      setAnswer({ id: `quick-${topic}`, status: 'answered', answer: text, question: label } as unknown as ChatRequest);
      void speakAnswer(text);
      setAsking(false);
    } catch {
      setAsking(false);
      await ask(label, false);
    }
  };

  // A page left with the microphone open is a page still listening.
  useEffect(
    () => () => {
      window.clearTimeout(settleTimerRef.current);
      listenRef.current?.stop();
    },
    [],
  );

  // Top right, below the topbar (bottom right above the tab bar on a
  // phone), until it is dragged somewhere else.
  useEffect(() => {
    const place = () => setPos((p) => p ?? startPos());
    place();
    const onResize = () =>
      setPos((p) =>
        p
          ? {
              x: Math.min(p.x, window.innerWidth - SIZE - EDGE),
              y: Math.min(p.y, maxY()),
            }
          : p,
      );
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!dragging.current) return;
      const far =
        Math.abs(e.clientX - origin.current.x) > DRAG_THRESHOLD ||
        Math.abs(e.clientY - origin.current.y) > DRAG_THRESHOLD;
      if (!far) return;
      moved.current = true;
      setPos({
        x: Math.max(EDGE, Math.min(e.clientX - grab.current.x, window.innerWidth - SIZE - EDGE)),
        y: Math.max(EDGE, Math.min(e.clientY - grab.current.y, maxY())),
      });
    };
    const onUp = () => {
      dragging.current = false;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, []);

  const brief = usePoll<{ text: string }>(() => api.get('/api/brief'), 300_000, ['brief']);

  if (!pos) return null;

  const onPointerDown = (e: React.PointerEvent) => {
    dragging.current = true;
    moved.current = false;
    origin.current = { x: e.clientX, y: e.clientY };
    grab.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
  };

  // A drag must not also count as a click, or moving it opens the panel.
  const onClick = () => {
    if (moved.current) return;
    setOpen((o) => !o);
  };

  const panelRight = pos.x > window.innerWidth / 2;

  return (
    <>
      <button
        type="button"
        className={`cc-fab${open ? ' open' : ''}`}
        style={{ left: pos.x, top: pos.y }}
        onPointerDown={onPointerDown}
        onClick={onClick}
        aria-expanded={open}
        aria-label="A.F.R.O.B.O.T"
        title="A.F.R.O.B.O.T — click to open, click and hold to move"
      >
        <img src="/afrobot.png" alt="" width={364} height={514} />
      </button>

      {open && (
        <section
          className="cc-fab-panel"
          style={{
            top: pos.y + SIZE + 10,
            ...(panelRight
              ? { right: Math.max(EDGE, window.innerWidth - pos.x - SIZE) }
              : { left: pos.x }),
          }}
          aria-label="A.F.R.O.B.O.T"
        >
          <header>
            <h3>A.F.R.O.B.O.T <small className="cc-bot-role">Reporting Agent</small></h3>
            <button
              type="button"
              className="cc-fab-close"
              onClick={() => {
                audioRef.current?.pause();
                setSpeaking(false);
                setOpen(false);
              }}
            >
              Close
            </button>
          </header>

          <button
            type="button"
            className={`cc-speak${speaking ? ' on' : ''}`}
            disabled={!brief.data?.text}
            onClick={async () => {
              if (speaking) {
                audioRef.current?.pause();
                setSpeaking(false);
                return;
              }
              const text = brief.data?.text;
              if (!text) return;
              setSpeakError(false);
              try {
                const blob = await api.postForBlob('/api/speak', { text });
                const url = URL.createObjectURL(blob);
                const el = new Audio(url);
                audioRef.current = el;
                el.onended = () => {
                  setSpeaking(false);
                  URL.revokeObjectURL(url);
                };
                await el.play();
                setSpeaking(true);
              } catch {
                setSpeakError(true);
                setSpeaking(false);
              }
            }}
          >
            {speaking ? 'Stop' : 'Read the brief aloud'}
          </button>
          <p className="cc-mic-note">
            {speakError
              ? 'The voice could not be reached just now. The brief above is unaffected.'
              : api.lastVoice === 'licensed'
                ? 'Licensed voice, for internal Addaiaspora use only.'
                : 'Placeholder voice (Polly Brian) until the licensed voice is set up.'}
          </p>

          <button
            type="button"
            className={`cc-mic${listening ? ' on' : ''}`}
            disabled={asking}
            onClick={async () => {
              // Pressing the button while listening means "I am done":
              // send what was heard now rather than discarding it, which
              // is what the natural push-to-talk press used to do.
              if (listening) {
                submitQuestion();
                return;
              }
              if (micBusyRef.current) return;
              micBusyRef.current = true;
              setListenError(null);
              setAnswer(null);
              setHeard('');
              setBlockedAnswer(null);
              setStillWorking(false);
              finalsRef.current = [];
              partialRef.current = '';
              submittedRef.current = false;
              // Or the microphone transcribes the voice mid-sentence:
              // echo cancellation is a reducer, not a wall.
              audioRef.current?.pause();
              setSpeaking(false);
              try {
                listenRef.current = await startListening(api, {
                  onPartial: (text) => {
                    if (submittedRef.current) return;
                    partialRef.current = text;
                    setHeard(heardSoFar());
                  },
                  onFinal: (text) => {
                    if (submittedRef.current) return;
                    finalsRef.current.push(text);
                    partialRef.current = '';
                    setHeard(heardSoFar());
                    // Not sent yet: a settled phrase can be a pause for
                    // breath, not the end of the question. Another phrase
                    // restarts the clock; a second and a half of quiet
                    // sends the lot.
                    window.clearTimeout(settleTimerRef.current);
                    settleTimerRef.current = window.setTimeout(submitQuestion, 1500);
                  },
                  onError: (message) => {
                    setListenError(message);
                    listenRef.current?.stop();
                    listenRef.current = null;
                    setListening(false);
                  },
                });
                setListening(true);
              } catch (err) {
                // Overwhelmingly a refused permission prompt, which is a
                // choice rather than a fault, so it does not read as one.
                setListenError(
                  err instanceof DOMException && err.name === 'NotAllowedError'
                    ? 'Chrome is blocking the microphone for this site. Allow it in the address bar and press again.'
                    : err instanceof Error && err.message.startsWith('The transcriber')
                      ? `${err.message} Try again.`
                      : 'The microphone could not be started.',
                );
              } finally {
                micBusyRef.current = false;
              }
            }}
          >
            {listening
              ? heard
                ? 'Done - ask it'
                : 'Stop listening'
              : asking
                ? 'Asking...'
                : 'Hold a question'}
          </button>
          <p className="cc-mic-note">
            {listenError ??
              (listening
                ? 'Listening. Speech goes to Transcribe in eu-west-2 and is not recorded.'
                : 'Ask out loud. It reaches the agent the same way a typed question does, and nothing it answers with executes on its own.')}
          </p>
          {heard ? <p className="cc-heard">&ldquo;{heard}&rdquo;</p> : null}
          {answer ? (
            <div className="cc-bubble">
              {answer.status === 'answered' ? (
                <p>{answer.answer}</p>
              ) : answer.status === 'refused' ? (
                <p>{answer.error ?? 'The agent declined that one.'}</p>
              ) : answer.status === 'failed' ? (
                <p>{answer.error ?? 'That question could not be answered.'}</p>
              ) : stillWorking ? (
                <p className="cc-bubble-empty">
                  Taking longer than usual. It is still working; the answer will appear in the
                  Chat tab.
                </p>
              ) : (
                <p className="cc-bubble-empty">
                  {`Thinking${elapsed > 2 ? ` - ${elapsed}s` : ''}. It reads the live numbers before it answers, which takes about half a minute.`}
                </p>
              )}
            </div>
          ) : null}
          {blockedAnswer ? (
            <button type="button" className="cc-speak" onClick={() => void speakAnswer(blockedAnswer)}>
              Hear the answer
            </button>
          ) : null}

          <div className="cc-bubble">
            {brief.error ? (
              <p className="cc-bubble-empty">
                The overnight summary could not be read just now. It is the same brief the 08:00
                Slack post sends, so that copy is unaffected.
              </p>
            ) : brief.data?.text ? (
              <p>{brief.data.text}</p>
            ) : (
              <p className="cc-bubble-empty">Reading the overnight summary…</p>
            )}
          </div>

          <div className="cc-prompts">
            {SUGGESTIONS.map((p) => (
              <button key={p.topic} type="button" className="cc-prompt" disabled={asking || listening} onClick={() => void quick(p.label, p.topic)}>
                {p.label}
              </button>
            ))}
          </div>
          <p className="cc-bot-note">
            Ask anything about the numbers, by voice or by tapping a question: sign-ups, purchases,
            downloads, tickets, what's on. It reads the live figures and never changes anything.{' '}
            <button type="button" className="cc-link" onClick={() => { setOpen(false); onAsk(''); }}>
              Open the chat
            </button>{' '}
            for longer questions or another agent.
          </p>
        </section>
      )}
    </>
  );
}
